/**
 * This helper handles the rules for the scenario payload. It imports all the rules modules declarations
 * and provides functions to handle them, mainly
 * - Functions to retrieve rules, check a scenario state, clean it. They're use a lot paths (for rules, fields, etc.)
 * which are string arrays representing the supposed path to reach a rule or the equivalent field.
 * - Functions to convert a rule into a v-validate expression.
 */

import _log from '@/helpers/log';
import { normalizeFloat } from '@/helpers/mathHelper';

import { IRule } from '@/classes/rules/IRule';
import { INestedRule } from '@/classes/rules/INestedRule';
import { BooleanRule } from '@/classes/rules/BooleanRule';
import { ObjectRule } from '@/classes/rules/ObjectRule';
import { NumberRule } from '@/classes/rules/NumberRule';
import { MinMaxRule } from '@/classes/rules/MinMaxRule';
import { TextRule } from '@/classes/rules/TextRule';
import { OptionsRule } from '@/classes/rules/OptionsRule';

import scenarioRules from '@/helpers/rulesModules/scenarioRules';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { FieldStatus } from '@/classes/FieldStatus';
import { FieldStateRef } from '@/classes/FieldReference';
import { DtoExt } from '@/classes/scenario/DtoExt';
import { RuleFieldMessage } from '@/classes/rules/RuleFieldMessage';
import { ObjectOnly } from '@/classes/ObjectOnly';
import { InternationalizedLabel } from '@/interfaces/InternationalizedLabel';
import { getValueFromPath } from '@/helpers/pathHelper';

interface Dependencies<Context> {
  rules: INestedRule<Context>
}

const defaultOptions: Dependencies<ScenarioExt> = {
  rules: scenarioRules
};

export const createRuleHelper = <Context = ScenarioExt>({ rules }: Dependencies<Context>) => {
// METHODS =========================================================
  /**
   * Check if a rule is a INestedRule (i.e it contains nested rules)
   * @param rule Checked rule
   */
  function isINestedRule(rule?: IRule<Context>): rule is INestedRule<Context> {
    return (rule instanceof ObjectRule); // TODO: better interface check.
  }

  /**
   * Gets the scenario default value, according to the rules.
   * Since some default values depend on the scenario state (which at fist doesn't exists),
   * the process takes two steps:
   * 1) initializing the scenario with the default values that doesn't depend on the scenario state
   * 2) create a new scenario with default values that depend on the previously created scenario state.
   */
  function getDefaultScenario(): ScenarioExt {
    const rootRule = getFromRulesDictionary([]);
    if (!(rootRule instanceof ObjectRule)) {
      return new ScenarioExt();
    }
    // we initialize default values with no scenario argument.
    const scenarioStaticDefaultValue = rootRule.getDefaultValue();
    const staticScenario = scenarioStaticDefaultValue && scenarioStaticDefaultValue.value;
    // then we set the dynamic default values based on the initialized static scenario.
    const scenarioDynamicDefaultValue = rootRule.getDefaultValue(staticScenario);
    const scenario = scenarioDynamicDefaultValue && scenarioDynamicDefaultValue.value;
    if (!(scenario instanceof ScenarioExt)) {
      return new ScenarioExt();
    }
    return scenario;
  }

  /**
   * Gets a specific rule from the imported dictionary by the rule path
   * @param path path of the rule
   */
  function getFromRulesDictionary(path: string[]): IRule<Context> | undefined {
    return removePrefixes(path)
      .reduce((rule: IRule<Context> | undefined, ruleName) => isINestedRule(rule) ? rule.getNestedRule(ruleName) : rule, rules);
  }

  function removePrefixes(path: string[]) {
    const [prefix, ...rest] = path;
    if (prefix === rules.key) {
      return rest;
    }
    return path;
  }

  /**
   * A recursive function that iterate on a context and get all the fieldStatuses of its fields and subfields.
   * @param context the analysed context
   * @param path the current path on which we're going to start the analysis (used for recursivity)
   */
  function getDtoFieldStatuses(context: Context, path: string[]): FieldStatus[] {
    // Initialise the fieldStatus collector
    let fieldStatuses: FieldStatus[] = [];
    // get the root object
    const currentDto = getValueFromPath(context, path);
    // if the root object is a ScenarioExt, its status hasn't been previously checked
    if (currentDto instanceof ScenarioExt) {
      fieldStatuses.push(getFieldStatus(context, path));
    }
    // if the root object is not a DtoExt, we just concat and return its status.
    if (!(currentDto instanceof DtoExt)) {
      return fieldStatuses.concat(getFieldStatus(context, path));
    }
    // No it is a DtoExt, so we know it has sub-properties. We're going to iterate on each key.
    const dtoKeys = Object.keys(currentDto);

    for (const k of dtoKeys) {

      // Get current property Path by duplicating base path and adding current key.
      const currentPath = Array.from(path);
      currentPath.push(k);

      // Get current field status to check if it is available or not.
      let currentFieldStatus = getFieldStatus(context, currentPath);

      // Check the field's fields must be controlled too - i.e the field is available and is a DtoExt
      const isAvailableAndHasSubProperties = currentFieldStatus.state !== FieldStateRef.unavailable &&
        getValueFromPath(context, currentPath) instanceof DtoExt;

      if (isAvailableAndHasSubProperties) {
        const subFieldStatuses = getDtoFieldStatuses(context, currentPath);
        fieldStatuses = fieldStatuses.concat(subFieldStatuses);

        currentFieldStatus = currentFieldStatus.addSubFieldStatuses(subFieldStatuses);
      }
      fieldStatuses.push(currentFieldStatus);
    }
    return fieldStatuses;
  }

  /**
   * Gets the field status of one field or subfield of the context. Called in the recursive function above.
   * @param context the analysed context
   * @param path path of the analysed field
   */
  function getFieldStatus(context: Context, path: string[]): FieldStatus {
    const key = path.length ? path[path.length - 1] : 'scenario'; // Key is the last property of the path.
    path = Array.from(path); // shallow copy as usual.
    const rule = getFromRulesDictionary(path);
    if (!rule) {
      return new FieldStatus({
        key,
        path,
        state: FieldStateRef.no_rule,
        msg: `Field has no rule`
      });
    }
    const ruleIsAvailable = rule.isAvailable(context);
    if (!ruleIsAvailable) {
      return new FieldStatus({
        key,
        path,
        state: FieldStateRef.unavailable,
        msg: `Field isn't available`
      });
    }
    const value = getValueFromPath(context, path);
    const isValid = rule.checkValue(value, context);
    if (value === undefined || isValid === null) {
      return new FieldStatus({
        key,
        path,
        state: FieldStateRef.not_set,
        msg: `Field is not set`,
        value,
        preventReport: rule.fieldUsage.getUsageStatus()
      });
    }
    if (isValid === false) {
      const ruleMessages = rule instanceof ObjectRule ? rule.getErrorMessages() : [];
      return new FieldStatus({
        key,
        path,
        state: FieldStateRef.error,
        msg: `Field has an incorrect value`,
        ruleMessages,
        value,
        preventReport: rule.fieldUsage.getUsageStatus()
      });
    }
    return new FieldStatus({
      key,
      path,
      state: FieldStateRef.valid,
      msg: `Field is valid`,
      value
    });
  }

  /**
   * "Cleans" a scenario based on an array of fieldStatuses:
   * If a field is supposed to not be available, it is set to "null"
   * @param scenario the cleaned scenario
   * @param fieldStatuses the fieldStatuses used to clean the scenario
   */
  function cleanScenario(scenario: ScenarioExt, fieldStatuses: FieldStatus[]) {
    const fieldsShouldBeNull = fieldStatuses.filter(s => s.state === FieldStateRef.unavailable);
    for (const field of fieldsShouldBeNull) {
      if (!field.path.length) {
        continue;
      }
      let currentField = scenario as any;
      const pathToLastObject = Array.from(field.path);
      const lastKey = pathToLastObject.pop();
      if (!lastKey) {
        continue;
      }
      for (const key of pathToLastObject) {
        currentField = currentField[key];
        if (!currentField) {
          break;
        }
      }
      if (!currentField) {
        _log.logError(`Impossible to clean field '${field.path.join('.')}, can't access the path'`);
        continue;
      }
      currentField[lastKey!] = null;
    }
  }

  // V-validate rules ===================================
  /**
   * These functions have all the same purpose: converting a rule into its v-validator equivalent expression
   * @param rule the converted rule
   */
  function getVValidateBooleanRule(rule: BooleanRule | undefined): string | ObjectOnly {
    return rule && rule.required ? 'required' : '';
  }

  function getVValidateNumberRule(rule: NumberRule | undefined): string | ObjectOnly {
    if (!rule) {
      return 'decimal';
    }
    const requiredString = rule.required ? 'required|' : '';
    return `${requiredString}decimal`;
  }

  /**
   * @deprecated use MinMaxRule.vValidateRule function instead
   */
  function getVValidateMinMaxRule(rule?: MinMaxRule): string | ObjectOnly {
    if (!rule) {
      return 'decimal';
    }
    let vValidateRule = '';
    vValidateRule += rule.required ? 'required|' : '';
    vValidateRule += rule.isInteger ? 'numeric|' : '';
    const minimumValue = typeof rule.min === 'number' ? normalizeFloat(rule.min * rule.factor, 3) : rule.min;
    const maximumValue = typeof rule.max === 'number' ? normalizeFloat(rule.max * rule.factor, 3) : rule.max;
    vValidateRule += maximumValue === undefined && minimumValue !== undefined ? `min_value: ${minimumValue}` : '';
    vValidateRule += minimumValue === undefined && maximumValue !== undefined ? `max_value: ${maximumValue}` : '';
    vValidateRule += minimumValue !== undefined && maximumValue !== undefined ? `between:${minimumValue},${maximumValue}` : '';
    return vValidateRule;
  }

  function getVValidateTextRule(
    rule: TextRule | undefined
  ): string | ObjectOnly | undefined {
    if (rule === undefined) {
      return undefined;
    }
    if (rule.regex !== undefined) {
      return {
        required: rule.required,
        regex: rule.regex
      };
    }
    return rule.required ? 'required' : undefined;
  }

  function getVValidateOptionsRule(
    rule: OptionsRule<any> | undefined,
    transformString?: (input: string) => string
  ): string | undefined {
    if (rule === undefined) {
      return undefined;
    }
    const requiredString = rule.required ? 'required|' : '';
    const optionStringvalues = rule.options.map((s: string) => {
      return transformString ? transformString(s) : s;
    });
    return `${requiredString}included:${optionStringvalues.join(',')}`;
  }

  function getVValidateAnyRule(rule: IRule | undefined): string | any | undefined {
    if (rule === undefined) {
      return undefined;
    }
    if (rule instanceof OptionsRule) {
      return getVValidateOptionsRule(rule);
    }
    if (rule instanceof MinMaxRule) {
      return getVValidateMinMaxRule(rule);
    }
    return undefined;
  }

  function computeNewErrors(newErrors?: RuleFieldMessage[], oldErrors?: RuleFieldMessage[]): Map<string, InternationalizedLabel | null> {
    const uniqMessagesByField = new Map();
    for (const error of oldErrors ?? []) {
      for (const field of error.fields) {
        uniqMessagesByField.set(field, null);
      }
    }
    for (const error of newErrors ?? []) {
      for (const field of error.fields) {
        uniqMessagesByField.set(field, error.message);
      }
    }
    return uniqMessagesByField;
  }

  return {
    getDefaultScenario,
    computeNewErrors,
    getFromRulesDictionary,
    getDtoFieldStatuses,
    cleanScenario,
    getVValidateBooleanRule,
    getVValidateNumberRule,
    getVValidateMinMaxRule,
    getVValidateTextRule,
    getVValidateOptionsRule,
    getVValidateAnyRule
  };
};
const rulesHelper = createRuleHelper(defaultOptions);
export default rulesHelper;
